<script setup>
// InteliChat MOBILE bottom navigation (phones < 768px only - the CSS
// hides this entirely at tablet/desktop widths, where the icon rail /
// full rail stays exactly as it is).
//
// Charlie's hybrid pattern (INTELICHAT-MOBILE-DESIGN-SPEC.md section 2
// + MOBILE-NAV-RULINGS.md, the design-lead refinement pass):
// a fixed bottom tab bar with the 4 primary agent tasks one-tap
// (Today / Inbox / Customers / Orders) + a 5th "More" slot that opens a
// bottom sheet with the rest, ordered by agent frequency (ruling 1):
// Tools = History (FIRST), Fitment, Recovery, Analytics; Manage =
// Contacts, Reports, Notifications, Settings; System = Classic view,
// then the agent profile/status row.
//
// Refinements folded in from MOBILE-NAV-RULINGS.md:
// - Inbox badge in the PINK alert accent (--il-accent-2), cap "9+",
//   hidden at 0 (purple stays the active-state color).
// - Pressed state (scale 0.96 + opacity) on tabs + sheet rows; the
//   active tab colors BOTH icon + label purple. Reduced-motion safe.
// - Sheet: slide-up 220ms ease-out + backdrop fade on open; dismiss
//   via swipe-down, backdrop tap, grabber, and the BACK button (a
//   history entry is pushed when the sheet opens, so back closes the
//   sheet first and only a second back navigates).
// - a11y: sheet = role="dialog" + aria-modal + aria-label + focus
//   trap (Tab cycles, Escape closes, focus restored to the More tab);
//   active tab = aria-current="page"; every control >= 44px hit area;
//   muted labels use the 4.5:1-checked --il-ink-3a token.
// - Icons: medium-weight strokes (2px) at a true 24px render so they
//   stay legible at 390 (corpus icon-stroke gate, delta 1).
// - Fluid type via clamp(); no gradients/glow on the nav (anti-slop).
//
// All state comes through the same intelichat adapters the rail uses
// (native Vuex underneath) - no forked data. The bar honors
// env(safe-area-inset-bottom); so does the sheet.
//
// The bar hides itself while a conversation is OPEN in the inbox
// (intelichat/inbox/:conversationId): the spec's mobile cockpit is a
// full-screen conversation with its own back affordance + pinned
// composer (wireframe 8.3 has no tab bar under the composer).
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useInteliAccount } from '../composables/useInteliAccount';
import { useInteliAgent } from '../composables/useInteliAgent';
import { useInteliConversation } from '../composables/useInteliConversation';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { accountId } = useInteliAccount();
const { displayName, initials, availabilityLabel, availabilityTone } =
  useInteliAgent();
const { mineCount } = useInteliConversation();

// ---- LIVE badge data (charlie research A4): the badge reads the same
// native conversationStats mineCount the IC rail + Inbox tab counts use
// (real /meta data via Vuex - never a hardcoded number). The nav itself
// keeps it fresh so the badge is correct even before a workspace has
// fetched: on mount, on every route change, and on a gentle phone-only
// 60s tick. Absent data reads as null -> badge hidden, never faked.
const STATS_PARAMS = {
  assigneeType: 'all',
  status: 'open',
  sortBy: 'last_activity_at_desc',
  page: 1,
};
const isPhone = () =>
  window.matchMedia('(max-width: 767.98px)').matches;
const refreshStats = () => {
  if (!isPhone()) return;
  Promise.resolve(
    store.dispatch('conversationStats/get', STATS_PARAMS)
  ).catch(() => {});
};
let statsTimer = null;

// ---- primary tabs (one-tap agent tasks) ----
const TABS = [
  { key: 'today', label: 'Today', routeName: 'intelichat_today' },
  { key: 'inbox', label: 'Inbox', routeName: 'intelichat_inbox' },
  { key: 'customers', label: 'Customers', routeName: 'intelichat_customers' },
  { key: 'orders', label: 'Orders', routeName: 'intelichat_orders' },
];

// ---- "More" sheet contents ----
// Ruling 1 order: History FIRST (Tyler just elevated it, #34), then the
// per-conversation lookups, Analytics last (manager-facing).
const MORE_TOOLS = [
  { key: 'history', label: 'History', routeName: 'intelichat_history' },
  { key: 'tickets', label: 'Tickets', routeName: 'intelichat_tickets' },
  { key: 'fitment', label: 'Fitment', routeName: 'intelichat_fitment' },
  { key: 'recovery', label: 'Recovery', routeName: 'intelichat_recovery' },
  { key: 'analytics', label: 'Analytics', routeName: 'intelichat_analytics' },
];

// native sections (render in-shell under the persistent IC chrome,
// exactly like the desktop rail's Manage group)
const MORE_MANAGE = [
  {
    key: 'contacts',
    label: 'Contacts',
    routeName: 'contacts_dashboard_index',
    path: '/contacts',
  },
  {
    key: 'reports',
    label: 'Reports',
    routeName: 'account_overview_reports',
    path: '/reports',
  },
  {
    key: 'notifications',
    label: 'Notifications',
    routeName: 'notifications_index',
    path: '/notifications',
  },
  {
    key: 'settings',
    label: 'Settings',
    routeName: 'settings_home',
    path: '/settings',
  },
];

const sheetOpen = ref(false);
const sheetEl = ref(null);
const moreBtn = ref(null);

const isTabActive = key => route.meta?.inteliNav === key;

const isMoreDestActive = computed(() => {
  const nav = route.meta?.inteliNav;
  if (nav) return MORE_TOOLS.some(t => t.key === nav);
  // native sections carry no inteliNav meta - match by path segment
  return MORE_MANAGE.some(m => route.path.includes(m.path));
});

// the conversation cockpit owns the full phone screen
const conversationOpen = computed(
  () =>
    route.name === 'intelichat_inbox' &&
    Boolean(route.params?.conversationId)
);

// ---- sheet open/close + back-button behavior (ruling: back closes the
// sheet FIRST, before navigating). Opening pushes a same-URL history
// entry; hardware/browser back pops it, the popstate handler closes the
// sheet, and the route never changes. Any other dismissal consumes the
// pushed entry with history.back() so history stays clean.
const SHEET_STATE = 'ilMoreSheet';

const openSheet = () => {
  sheetOpen.value = true;
  try {
    window.history.pushState(
      { ...(window.history.state || {}), [SHEET_STATE]: true },
      ''
    );
  } catch {
    // history unavailable: sheet still opens/closes without back support
  }
  nextTick(() => sheetEl.value?.focus());
};

const closeSheet = () => {
  if (!sheetOpen.value) return;
  if (window.history.state?.[SHEET_STATE]) {
    // popstate handler flips sheetOpen off
    window.history.back();
  } else {
    sheetOpen.value = false;
  }
  nextTick(() => moreBtn.value?.focus());
};

const onPopstate = () => {
  if (sheetOpen.value) sheetOpen.value = false;
};
onMounted(() => {
  window.addEventListener('popstate', onPopstate);
  refreshStats();
  statsTimer = setInterval(refreshStats, 60000);
});
onBeforeUnmount(() => {
  window.removeEventListener('popstate', onPopstate);
  if (statsTimer) clearInterval(statsTimer);
});
watch(
  () => route.name,
  () => refreshStats()
);

const goTo = routeName => {
  const viaSheetEntry = Boolean(window.history.state?.[SHEET_STATE]);
  sheetOpen.value = false;
  const target = {
    name: routeName,
    params: { accountId: accountId.value },
  };
  // replace the synthetic sheet entry so back returns to the previous
  // screen, not to a phantom same-URL state
  if (viaSheetEntry) router.replace(target);
  else router.push(target);
};

const openClassic = () => {
  const viaSheetEntry = Boolean(window.history.state?.[SHEET_STATE]);
  sheetOpen.value = false;
  // same explicit Classic-view bypass as the rail (?classic=1 sticky)
  const target = {
    name: 'home',
    params: { accountId: accountId.value },
    query: { classic: '1' },
  };
  if (viaSheetEntry) router.replace(target);
  else router.push(target);
};

// ---- focus trap (sheet = role="dialog"; Tab cycles, Escape closes) ----
const onSheetKeydown = e => {
  if (e.key === 'Escape') {
    e.stopPropagation();
    closeSheet();
    return;
  }
  if (e.key !== 'Tab' || !sheetEl.value) return;
  const focusables = sheetEl.value.querySelectorAll(
    'button, [href], [tabindex]:not([tabindex="-1"])'
  );
  if (!focusables.length) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  } else if (!sheetEl.value.contains(document.activeElement)) {
    e.preventDefault();
    first.focus();
  }
};

// ---- swipe-down to dismiss (only when the sheet is scrolled to top,
// so it never fights the sheet's own scroll) ----
const dragY = ref(0);
let dragStartY = null;

const onTouchStart = e => {
  if (!sheetEl.value || sheetEl.value.scrollTop > 0) return;
  dragStartY = e.touches[0].clientY;
};
const onTouchMove = e => {
  if (dragStartY == null) return;
  dragY.value = Math.max(0, e.touches[0].clientY - dragStartY);
};
const onTouchEnd = () => {
  const shouldClose = dragY.value > 70;
  dragY.value = 0;
  dragStartY = null;
  if (shouldClose) closeSheet();
};

const dragStyle = computed(() =>
  dragY.value
    ? { transform: `translateY(${dragY.value}px)`, transition: 'none' }
    : null
);

const inboxBadge = computed(() => mineCount.value);
// ruling: cap the badge at "9+"
const inboxBadgeText = computed(() =>
  inboxBadge.value > 9 ? '9+' : String(inboxBadge.value)
);
const inboxAria = computed(() =>
  inboxBadge.value
    ? `Inbox, ${inboxBadge.value} conversation${
        inboxBadge.value === 1 ? '' : 's'
      } need you`
    : 'Inbox'
);

// route change (e.g. tab tap elsewhere) always dismisses the sheet
watch(
  () => route.fullPath,
  () => {
    sheetOpen.value = false;
  }
);
</script>

<template>
  <nav
    v-if="!conversationOpen"
    class="il-bottomnav"
    data-il="mobile-bottomnav"
    aria-label="InteliChat mobile navigation"
  >
    <button
      v-for="tab in TABS"
      :key="tab.key"
      class="bn-tab"
      :class="{ on: isTabActive(tab.key) }"
      :data-il-bn="tab.key"
      :aria-current="isTabActive(tab.key) ? 'page' : undefined"
      :aria-label="tab.key === 'inbox' ? inboxAria : undefined"
      @click="goTo(tab.routeName)"
    >
      <span class="bn-icwrap">
        <svg
          v-if="tab.key === 'today'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <path d="M9 22V12h6v10" />
        </svg>
        <svg
          v-else-if="tab.key === 'inbox'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path
            d="M21 11.5a8.5 8.5 0 0 1-8.5 8.3 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 1 1 17 0z"
          />
        </svg>
        <svg
          v-else-if="tab.key === 'customers'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="9" cy="8" r="4" />
          <path
            d="M2 21a7 7 0 0 1 14 0M17 11a4 4 0 0 0 0-6M22 21a6 6 0 0 0-4-5.6"
          />
        </svg>
        <svg
          v-else
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <path d="M3 6h18M16 10a4 4 0 0 1-8 0" />
        </svg>
        <span
          v-if="tab.key === 'inbox' && inboxBadge"
          class="bn-badge il-mono"
          data-il="bn-inbox-badge"
          aria-hidden="true"
          >{{ inboxBadgeText }}</span
        >
      </span>
      <span class="bn-lbl">{{ tab.label }}</span>
    </button>

    <button
      ref="moreBtn"
      class="bn-tab"
      :class="{ on: isMoreDestActive || sheetOpen }"
      data-il-bn="more"
      aria-haspopup="dialog"
      :aria-expanded="sheetOpen ? 'true' : 'false'"
      @click="openSheet"
    >
      <span class="bn-icwrap">
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="2.1" />
          <circle cx="12" cy="12" r="2.1" />
          <circle cx="19" cy="12" r="2.1" />
        </svg>
      </span>
      <span class="bn-lbl">More</span>
    </button>
  </nav>

  <!-- "More" bottom sheet -->
  <div
    v-if="sheetOpen"
    class="il-moresheet"
    data-il="mobile-moresheet"
    @click.self="closeSheet"
  >
    <div
      ref="sheetEl"
      class="ms-sheet"
      role="dialog"
      aria-modal="true"
      aria-label="More navigation"
      tabindex="-1"
      :style="dragStyle"
      @keydown="onSheetKeydown"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
    >
      <button class="ms-grab" aria-label="Close menu" @click="closeSheet">
        <span aria-hidden="true" />
      </button>

      <div class="ms-h">Tools</div>
      <button
        v-for="item in MORE_TOOLS"
        :key="item.key"
        class="ms-row"
        :class="{ on: route.meta?.inteliNav === item.key }"
        :data-il-ms="item.key"
        :aria-current="route.meta?.inteliNav === item.key ? 'page' : undefined"
        @click="goTo(item.routeName)"
      >
        <svg
          v-if="item.key === 'history'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
          <path d="M3 3v5h5" />
          <path d="M12 7v5l3.5 2" />
        </svg>
        <svg
          v-else-if="item.key === 'fitment'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="3" />
          <path
            d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2"
          />
        </svg>
        <svg
          v-else-if="item.key === 'tickets'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path
            d="M4 5h16a1 1 0 0 1 1 1v3a2 2 0 0 0 0 4v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3a2 2 0 0 0 0-4V6a1 1 0 0 1 1-1z"
          />
          <path d="M13 5v14" stroke-dasharray="2 2" />
        </svg>
        <svg
          v-else-if="item.key === 'recovery'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
          <path d="M3 3v5h5" />
        </svg>
        <svg
          v-else
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M3 3v18h18M18 17V9M13 17V5M8 17v-3" />
        </svg>
        <span class="lbl">{{ item.label }}</span>
      </button>

      <div class="ms-h ms-div">Manage</div>
      <button
        v-for="item in MORE_MANAGE"
        :key="item.key"
        class="ms-row"
        :class="{ on: !route.meta?.inteliNav && route.path.includes(item.path) }"
        :data-il-ms="item.key"
        @click="goTo(item.routeName)"
      >
        <svg
          v-if="item.key === 'contacts'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="7" r="4" />
          <path d="M4 21a8 8 0 0 1 16 0" />
        </svg>
        <svg
          v-else-if="item.key === 'reports'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M21 12a9 9 0 1 1-9-9" />
          <path d="M12 3a9 9 0 0 1 9 9h-9z" />
        </svg>
        <svg
          v-else-if="item.key === 'notifications'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path
            d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0"
          />
        </svg>
        <svg
          v-else
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="3" />
          <path
            d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5h.1a1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"
          />
        </svg>
        <span class="lbl">{{ item.label }}</span>
      </button>

      <div class="ms-h ms-div">System</div>
      <button class="ms-row" data-il-ms="native" @click="openClassic">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="3" width="18" height="18" rx="3" />
          <path d="M3 9h18M9 21V9" />
        </svg>
        <span class="lbl">Classic view</span>
      </button>

      <!-- agent profile / status (spec: sheet carries Profile/status) -->
      <div class="ms-me" data-il="ms-agent-identity">
        <span class="av">{{ initials }}</span>
        <div class="b">
          <div class="nm">{{ displayName }}</div>
          <div class="rl">
            <i :class="availabilityTone" />{{ availabilityLabel }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Hidden by default: the bottom nav exists ONLY on phones (<768px).
   Tablet (768-900) keeps the compact icon rail; desktop the full rail. */
.il-bottomnav,
.il-moresheet {
  display: none;
}

@media (max-width: 767.98px) {
  .il-bottomnav {
    display: flex;
    align-items: stretch;
    background: var(--il-card);
    border-top: 1px solid var(--il-line);
    /* metallic top edge, same premium layer as the topbar's bottom edge */
    box-shadow: 0 -1px 0 var(--il-line);
    padding-bottom: env(safe-area-inset-bottom, 0px);
    z-index: 30;
  }

  /* .il-bottomnav prefix is deliberate: the tokens-layer button reset
     (.il-app button:not(...) { color: inherit }, specificity 0,2,2)
     out-specifies a bare scoped .bn-tab (0,2,0), which silently turned
     the muted labels full-ink. The extra class wins (0,3,0). */
  .il-bottomnav .bn-tab {
    flex: 1;
    min-height: 56px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    /* a11y: 4.5:1-checked muted (plain --il-ink-3 measures 3.4:1) */
    color: var(--il-ink-3a, var(--il-ink-2));
    padding: 6px 0 5px;
    transition: transform 0.1s ease, opacity 0.1s ease;

    /* icon-stroke gate (delta 1): true 24px render, medium 2px stroke */
    svg {
      width: 24px;
      height: 24px;
    }

    .bn-icwrap {
      position: relative;
      display: grid;
      place-items: center;
      width: 44px;
      height: 28px;
      border-radius: 14px;
      transition: background 0.14s;
    }

    .bn-lbl {
      /* fluid type (tier 0): clamp, never bare vw */
      font-size: clamp(10px, 2.8vw, 12px);
      font-weight: 600;
      letter-spacing: 0.01em;
    }

    .bn-badge {
      position: absolute;
      top: -4px;
      right: 1px;
      min-width: 16px;
      height: 16px;
      padding: 0 4px;
      border-radius: 10px;
      /* ruling: badge = PINK alert accent, NOT purple (purple = active) */
      background: var(--il-accent-2);
      color: #fff;
      font-size: clamp(9px, 2.5vw, 10px);
      font-weight: 700;
      display: grid;
      place-items: center;
      line-height: 1;
    }

    /* pressed state (ruling: brief scale + opacity on tap) */
    &:active {
      transform: scale(0.96);
      opacity: 0.8;
    }

    /* active = purple icon + label + pill (both colored) */
    &.on {
      color: var(--il-accent);

      .bn-icwrap {
        background: var(--il-accent-soft);
      }

      .bn-lbl {
        font-weight: 700;
      }
    }

    &:focus-visible {
      outline: 2px solid var(--il-accent);
      outline-offset: -2px;
      box-shadow: none;
      border-radius: 10px;
    }
  }

  /* ---- More sheet ---- */
  .il-moresheet {
    display: block;
    position: fixed;
    inset: 0;
    background: rgb(20 22 43 / 34%);
    z-index: 70;
    /* backdrop fade (ruling: sheet motion) */
    animation: il-ms-fade 0.2s ease-out;
  }

  .ms-sheet {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    max-height: min(76dvh, 620px);
    overflow-y: auto;
    overscroll-behavior: contain;
    background: var(--il-card);
    border-radius: 16px 16px 0 0;
    border-top: 1px solid var(--il-line-2);
    box-shadow: var(--il-sh-lg);
    padding: 4px 10px calc(14px + env(safe-area-inset-bottom, 0px));
    /* slide-up 220ms ease-out (ruling: 200-250ms); reduced-motion is
       killed globally in tokens.scss (animation: none !important) */
    animation: il-ms-up 0.22s ease-out;
    touch-action: pan-y;

    &:focus-visible {
      outline: none;
    }
  }

  @keyframes il-ms-up {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  @keyframes il-ms-fade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .ms-grab {
    display: grid;
    place-items: center;
    width: 100%;
    /* 44px hit-slop on the grabber (visual bar stays 4px) */
    min-height: 44px;
    padding: 8px 0 2px;

    span {
      width: 40px;
      height: 4px;
      border-radius: 4px;
      background: var(--il-silver);
    }

    &:focus-visible {
      outline: 2px solid var(--il-accent);
      outline-offset: -2px;
      box-shadow: none;
      border-radius: 10px;
    }
  }

  .ms-h {
    font-size: clamp(10px, 2.8vw, 12px);
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    /* a11y: 4.5:1-checked muted for the group labels */
    color: var(--il-ink-3a, var(--il-ink-2));
    padding: 12px 10px 5px;
  }

  /* group divider (ruling 1: clusters keep dividers + labels) */
  .ms-div {
    border-top: 1px solid var(--il-line);
    margin-top: 8px;
  }

  /* same specificity note as .il-bottomnav .bn-tab above */
  .ms-sheet .ms-row {
    display: flex;
    align-items: center;
    gap: 13px;
    width: 100%;
    min-height: 48px;
    text-align: left;
    padding: 6px 12px;
    border-radius: var(--il-r-s);
    color: var(--il-ink-2);
    font-size: clamp(14px, 4vw, 16px);
    font-weight: 500;
    transition: transform 0.1s ease;

    svg {
      width: 20px;
      height: 20px;
      color: var(--il-ink-3a, var(--il-ink-2));
      flex-shrink: 0;
    }

    .lbl {
      flex: 1;
    }

    /* pressed state (ruling) */
    &:active {
      background: var(--il-paper);
      transform: scale(0.98);
    }

    &.on {
      background: var(--il-accent-soft);
      color: var(--il-accent);
      font-weight: 600;

      svg {
        color: var(--il-accent);
      }
    }

    &:focus-visible {
      outline: 2px solid var(--il-accent);
      outline-offset: -2px;
      box-shadow: none;
    }
  }

  .ms-me {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 12px 2px 0;
    padding: 12px;
    border-radius: var(--il-r-s);
    border: 1px solid var(--il-line);

    .av {
      width: 40px;
      height: 40px;
      font-size: 14px;
      border-radius: 50%;
      display: grid;
      place-items: center;
      font-weight: 600;
      flex-shrink: 0;
      font-family: var(--il-fd);
      background: var(--il-accent-soft);
      color: var(--il-accent);
    }

    .nm {
      font-size: clamp(13.5px, 3.8vw, 15px);
      font-weight: 600;
      line-height: 1.2;
      color: var(--il-ink);
    }

    .rl {
      font-size: clamp(12px, 3.3vw, 13.5px);
      color: var(--il-ink-3a, var(--il-ink-2));
      display: flex;
      align-items: center;
      gap: 5px;

      i {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--il-ink-4);
        display: inline-block;

        &.good {
          background: var(--il-good);
        }
        &.warn {
          background: var(--il-warn);
        }
      }
    }
  }

  /* reduced motion: tokens.scss already kills animations/transitions
     inside .il-app; also drop the pressed-state TRANSFORM itself */
  @media (prefers-reduced-motion: reduce) {
    .il-bottomnav .bn-tab:active,
    .ms-sheet .ms-row:active {
      transform: none;
    }
  }
}
</style>
